import {Injectable, OnDestroy} from '@angular/core';

import {Observable, ReplaySubject, Subject} from 'rxjs';
import {SmagApiService} from '../api/smag-api.service';
import {LoginService} from './login.service';
import {Capabilities as PlanningCapabilities} from 'smag-commons/dto/planning';
import {Capabilities as PredictionCapabilities} from 'smag-commons/dto/prediction';


/**
 * Api service that deals with prediction server and planner's capabilities.
 */
@Injectable()
export class CapabilitiesService implements OnDestroy {

    private destroyed = new Subject<void>();
    private planningCapsSubj: ReplaySubject<PlanningCapabilities>;
    private predictionCapsSubj: ReplaySubject<PredictionCapabilities>;

    constructor(private readonly apiService: SmagApiService,
                private readonly loginService: LoginService) {
        this.planningCapsSubj = new ReplaySubject<PlanningCapabilities>(1); // like BehaviorSubject but with no need for initial value
        this.predictionCapsSubj = new ReplaySubject<PredictionCapabilities>(1);
        // reload is triggered every time a user gets logged-in (different principals may have different permissions)
        loginService.addSetupHook(1, profile => {
            // NOTE: hierarchy is actually not needed here, but we do not want to wait for completion
            // of network loading before setting up connector devices
            console.debug(`Retrieving prediction/planning capabilities for user ${profile?.username}`);
            return Promise.all([this.reloadPlanningCaps(), this.reloadPredictionCaps()]);
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get planningCaps(): Observable<PlanningCapabilities> {
        return this.planningCapsSubj.asObservable();
    }

    get predictionCaps(): Observable<PredictionCapabilities> {
        return this.predictionCapsSubj.asObservable();
    }

    private reloadPlanningCaps(): Promise<PlanningCapabilities> {
        return this.apiService.getPlanningCapabilities().toPromise()
            .then(caps => {
                this.planningCapsSubj.next(caps);
                return caps;
            });
    }

    private reloadPredictionCaps(): Promise<PredictionCapabilities> {
        return this.apiService.getPredictionCapabilities().toPromise()
            .then(caps => {
                this.predictionCapsSubj.next(caps);
                return caps;
            });
    }
}
